import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { DateTime } from "luxon"
import { useNavigate } from "react-router-dom"

//graphql
import { useLazyQuery } from "@apollo/client"
import { GET_STAFF } from "./notificationsQueries"

//Helpers
import { GetLogLevelKey, LogError, LogWarning } from "../../helpers/logger"

//SignalR
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr"

//Notifications modules
import { notificationsUrl } from "../../config/microservicesUrls"
import {
  noteApiCountPost,
  noteApiListMutedPost,
  noteApiMarkAsReadPut,
} from "../notifications/notificationsApi"
import NotificationsToastContent from "../notifications/notificationsToastContent"
import NotificationsDialog from "./NotificationsDialog"

// Context
import { UserContext } from "../../context/userContext"

//PrimeReact
import { Badge } from "primereact/badge"
import { Toast } from "primereact/toast"

//styling
import ROUTES from "../Routes/routesContants"
import {
  notificationHasTags,
  replaceNotificationTags,
} from "./notificationsTags"
import {
  handleIncomingChatMessage,
  handleReferralNavigation,
  handleScheduledAssessment,
} from "./actionUtils"

export default function Notifications() {
  const userCtx = useContext(UserContext)
  const navigate = useNavigate()
  const pid = userCtx.participant_id
  const organization_id = "All"
  const enterprise_id = "All"
  const scheduledAssessmentActionUrl = "#ScheduledAssessment"
  const [notificationsCount, setNotificationsCount] = useState(null)
  const [errorState, setErrorState] = useState(null)
  const [refreshCount, setRefreshCount] = useState(new Date().getTime())
  const [externalAction, setExternalAction] = useState(undefined)
  const toast = useRef(null)
  const systemNotificationsFilter = ["Participants", "All"]
  const [messages, setMessages] = useState([])
  const [notificationsDialogVisible, setNotificationsDialogVisible] =
    useState(false)
  const pendingTasksCount = useRef(userCtx.pendingTasksCount)

  // SignalR
  const isReconnecting = useRef(false)
  const [connection, setConnection] = useState(null)

  useEffect(() => {
    pendingTasksCount.current = userCtx.pendingTasksCount
  }, [userCtx.pendingTasksCount])

  const setupSignalRConnection = async () => {
    try {
      const userDetail = `[PARTICIPANT] ${userCtx.participant_id} - ${userCtx.participant_name_first} ${userCtx.participant_name_last}`

      //Set isReconnecting to false, because we will be creating a new connection now.
      isReconnecting.current = false

      // Setup Connection
      const connection = new HubConnectionBuilder()
        .withUrl(`${notificationsUrl()}/hubs/notifications?user=${userDetail}`)
        .withAutomaticReconnect()
        .configureLogging(
          GetLogLevelKey() === "debug" ? LogLevel.Debug : LogLevel.Warning,
        )
        .build()

      connection.onreconnecting((error) => {
        LogWarning(`Notification hub reconnecting to server`, error)
        isReconnecting.current = true
      })

      connection.onreconnected(() => {
        // Update notification counter
        getNotificationsCount(false)
        isReconnecting.current = false
      })

      connection.onclose((error) => {
        if (isReconnecting.current || error) {
          isReconnecting.current = true
          const errorMessage = `Notifications have been disconnected, please refresh your browser to reconnect`
          setErrorState(errorMessage)
          //If the connection closed due to an error, attempt to reconnect
          LogWarning(
            errorMessage,
            isReconnecting.current
              ? "Unable to reconnect to the Notification Hub"
              : error,
          )
          restartNotificationHub(connection)
        }
      })

      // Register event handlers
      connection.on("NotificationEvent", (notification) => {
        handleNotificationEvent(notification)
      })

      // Start the connection
      await connection.start()

      setConnection(connection) // Save connection to state
    } catch (error) {
      setConnection(null)
      LogError(
        "Unable to connect to to Notification Hub, retrying in 10 seconds",
        error,
      )
      restartNotificationHub(null)
    }
  }

  const restartNotificationHub = (connection, timeOutPeriod = 10000) => {
    setTimeout(async () => {
      try {
        if (connection) {
          await connection.start()
          setErrorState(null)
          getNotificationsCount(true)
        } else {
          setupSignalRConnection()
        }
      } catch (error) {
        LogError("Unable to connect to the Notifications hub", error)
        showError(
          "Unable to connect to the Notifications hub. Please refresh the page.",
        )
        restartNotificationHub(connection, 30000) //Retry after 30s
      }
    }, timeOutPeriod) // Restart connection after 10 seconds or as long as requested.
  }

  const [getStaff] = useLazyQuery(GET_STAFF, {
    fetchPolicy: "cache-and-network",
    variables: {
      staff_id: undefined,
      organization_id: undefined,
    },
  })

  function notificationToUser(notification) {
    //Check by UserId
    if (notification?.userId) {
      return (
        notification.userId.toString() === pid.toString() ||
        notification.userId.toString() === userCtx.participant_alias
      )
    }
  }

  function systemNotificationToUserGroup(notification) {
    return (
      userCtx.participant_organizations?.some(
        (x) => x.organization_id === notification.groupId.toString(),
      ) &&
      notification.systemNotification === true &&
      systemNotificationsFilter.some(
        (filter) => filter === notification.systemNotificationFilter,
      )
    )
  }

  function systemNotificationToUserEnterprise(notification) {
    return (
      notification.systemNotification === true &&
      systemNotificationsFilter.some(
        (filter) => filter === notification.systemNotificationFilter,
      )
    )
  }

  async function checkNotificationSoundMuted(eventName) {
    try {
      let _mutedNotifications = !userCtx.mutedNotifications
        ? await getMutedNotifications()
        : userCtx.mutedNotifications

      if (_mutedNotifications?.mutedEventTypes?.length > 0) {
        const eventType = _mutedNotifications?.mutedEventTypes.find(
          (x) => x.name === eventName,
        )

        if (eventType && eventType.sound) {
          return true
        }
      }
    } catch (error) {
      LogError(error)
    }

    return false
  }

  async function checkNotificationMuted(eventName) {
    try {
      let _mutedNotifications = !userCtx.mutedNotifications
        ? await getMutedNotifications()
        : userCtx.mutedNotifications

      if (_mutedNotifications?.length > 0) {
        const eventType = _mutedNotifications?.mutedEventTypes.find(
          (x) => x.name === eventName,
        )

        if (eventType && eventType.channelSignalR) {
          return true
        }
      }
    } catch (error) {
      LogError(`Unable to check if notification is muted`, error)
    }

    return false
  }

  async function getMutedNotifications() {
    try {
      const res = await noteApiListMutedPost(
        userCtx.tokens.getNotificationToken(),
        organization_id,
        pid,
      )

      if (res) {
        userCtx.setMutedNotifications(res)
        return res
      }
    } catch (error) {
      LogError(`Unable to get notification mute settings`, error)
      toast.error((t) => (
        <NotificationsToastContent toast={t} systemNotification>
          Unable to get notification mute settings. See console for details.
        </NotificationsToastContent>
      ))

      return []
    }
  }

  async function findStaff(staff_id) {
    let result = undefined
    const staff = await getStaff({
      variables: {
        staff_id: staff_id,
      },
    })

    if (!!staff.data?.user && staff.data?.user.length > 0) {
      result = staff.data.user[0]
    }

    return result
  }

  const closeMessage = useCallback(
    (toastId) => {
      setMessages((prevMessages) => {
        const targetMessage = prevMessages.find((msg) => msg.detail === toastId)

        if (targetMessage) {
          toast.current.remove(targetMessage)
        }

        return prevMessages.filter((msg) => msg.detail !== toastId)
      })
    },
    [toast],
  )

  async function handleNotificationEvent(notification) {
    if (notification.primaryActionUrl === "#assigned-tasks") {
      pendingTasksCount.current = pendingTasksCount.current + 1
      userCtx.setPendingTasksCount(pendingTasksCount.current)
    }

    // Filter events to active group and user
    const noteToUser = notificationToUser(notification)
    const sysNotificationToUserGroup =
      systemNotificationToUserGroup(notification)
    const sysNotificationToUserEnterprise =
      systemNotificationToUserEnterprise(notification)

    if (
      noteToUser ||
      sysNotificationToUserGroup ||
      sysNotificationToUserEnterprise
    ) {
      const notificationMuted = await checkNotificationMuted(
        notification.eventType,
      )

      if (
        (notification.highPriority || notification.showToast) &&
        !notificationMuted
      ) {
        //Check description for system tags
        if (notificationHasTags(notification)) {
          const participantList = [
            {
              participant_id: userCtx.participant_id,
              participant_first_name: userCtx.participant_first_name,
              participant_last_name: userCtx.participant_last_name,
            },
          ]

          notification = await replaceNotificationTags(
            notification,
            participantList,
            undefined,
            undefined,
            undefined,
            findStaff,
          )
        }

        const notificationSoundMuted = await checkNotificationSoundMuted(
          notification.eventType,
        )

        const id = Date.now()
        const newMessage = {
          severity: "info",
          detail: id,
          closable: false,
          life:
            notification.primaryActionUrl || notification.secondaryActionUrl
              ? 30000
              : 15000,
          content: (
            <NotificationsToastContent
              toast={toast}
              toastId={id}
              notificationId={notification.id}
              notification={notification}
              systemNotification={notification.systemNotification}
              title={notification.title}
              description={notification.description}
              primaryActionLabel={notification.primaryActionLabel}
              primaryActionUrl={notification.primaryActionUrl}
              secondaryActionLabel={notification.secondaryActionLabel}
              secondaryActionUrl={notification.secondaryActionUrl}
              onHandleActionURL={handleActionURL}
              markNotificationRead={markAsRead}
              muteSound={notificationSoundMuted}
              onClose={closeMessage}
            >
              <div>
                <div className="mb-2">
                  <b>{notification.title}</b>
                </div>
                {notification.description}
              </div>
            </NotificationsToastContent>
          ),
        }

        setMessages((prev) => [...prev, newMessage])

        // Show toast message
        toast.current.show(newMessage)
      }

      // Update notification counter
      getNotificationsCount()
    }
  }

  const handleShowSignatureRequests = (nid) => {
    navigate(ROUTES.PARTICIPANT_DOCUMENTS, {
      replace: true,
      state: { overrideDefaultFilter: "awaiting_signature" },
    })
    setNotificationsDialogVisible(false)
  }

  const handleActionURL = async (actionUrl, notificationId, notification) => {
    //Special overrides
    if (actionUrl.startsWith("participant/referrals/detail"))
      actionUrl = "#GoToReferral"

    switch (actionUrl) {
      //Assessments actions
      case scheduledAssessmentActionUrl:
        await handleScheduledAssessment(notification, navigate)
        setNotificationsDialogVisible(false)
        break
      case "#ShowNotifications":
        setNotificationsDialogVisible(true)
        break
      case "#ShowNotificationSettings":
        setNotificationsDialogVisible(false)
        return navigate(`${ROUTES.PREFERENCES_NOTIFICATIONS}`)
      case "#assigned-tasks":
        userCtx.setTasksDialogVisible(true)
        if (notificationId) markAsRead(notificationId)
        break
      case "#created-tasks-pending":
        break
      case "#created-tasks-completed":
        break
      case "#signature-request-sent":
      case "#signature-request-signed":
      case "#signature-request-all-signed":
      case "#signature-request-declined":
      case "#signature-request-canceled":
        // if (notificationId) MarkAsRead(notificationId);
        handleShowSignatureRequests(notificationId)
        break
      case "#notifications":
      case "#go-to-chatconv":
        handleIncomingChatMessage(notification, navigate)
        break
      case "#sys-notifications":
      case "#mute":
        return navigate(`${ROUTES.PREFERENCES_NOTIFICATIONS}`)
      case "#GoToReferral":
        handleReferralNavigation(notification, navigate)
        setNotificationsDialogVisible(false)
        break
      default:
        if (actionUrl.includes("http")) {
          window.location.href = actionUrl
        } else {
          navigate(actionUrl)
        }
    }
  }

  if (!!externalAction) {
    const action = externalAction
    setExternalAction(undefined)
    if (!action.actionUrl) {
      LogError("Unable to handle action, no action specified")
    } else {
      handleActionURL(
        action.actionUrl,
        undefined,
        {
          primaryActionUrl: action.actionUrl,
          primaryActionPayload: action.payload,
        },
        undefined,
      )
    }
    userCtx.setNotificationAction(undefined)
  }

  const showError = useCallback(
    (message) => {
      const id = Date.now()
      if (toast.current) {
        toast.current.show({
          severity: "error",
          detail: id,
          closable: false,
          sticky: isReconnecting.current,
          content: (
            <NotificationsToastContent
              toast={toast}
              systemNotification
              isError
              toastId={id}
              description={message || errorState}
              isInfo={true}
            />
          ),
        })
      }
    },
    [errorState],
  )

  const showInfo = (message) => {
    const id = Date.now()
    //If the received message is a string, or does not have a message field, process it normally
    if (
      typeof message === "string" ||
      message instanceof String ||
      !message.message
    ) {
      toast.current.show({
        severity: "info",
        detail: id,
        closable: false,
        content: (
          <NotificationsToastContent
            toast={toast}
            systemNotification
            toastId={id}
            description={message}
            isInfo={true}
          />
        ),
      })
    } else {
      const messageValue = message.message
      const toastConfig = { ...message, message: undefined }

      toast.current.show({
        ...toastConfig,
        severity: "info",
        detail: id,
        closable: false,
        content: (
          <NotificationsToastContent
            toast={toast}
            systemNotification
            toastId={id}
            description={messageValue}
            isInfo={true}
          />
        ),
      })
    }
  }

  const handleButtonClick = () => {
    if (errorState) {
      showError()
    } else {
      setNotificationsDialogVisible(true)
    }
  }

  async function markAsRead(notificationId) {
    if (notificationId) {
      try {
        await noteApiMarkAsReadPut(
          userCtx.tokens.getNotificationToken(),
          notificationId,
        )
        getNotificationsCount()
      } catch (error) {
        LogError(
          `Unable to mark notification as read (id: ${notificationId})`,
          error,
        )
        toast.error((t) => (
          <notificationsToastContent toast={t} systemNotification>
            {`Unable to mark notification as read (id: ${notificationId}). See console for details.`}
          </notificationsToastContent>
        ))
      }
    }
  }

  const getNotificationsCount = useCallback(
    async (notifyNewCount) => {
      try {
        if (userCtx.participant_organizations?.length > 0) {
          const res = await noteApiCountPost(
            userCtx.tokens.getNotificationToken(),
            enterprise_id || "All",
            userCtx.participant_organizations.map((x) => x.organization_id),
            pid,
            userCtx.participant_alias,
            DateTime.fromISO(userCtx.participant_create_date).toJSDate(),
          )

          if (res) {
            if (
              notifyNewCount &&
              res?.numTotal !== notificationsCount?.numTotal &&
              res?.numTotal > 0
            ) {
              showInfo(
                "New notifications received, please open the Notification window for more information",
              )
            }

            setNotificationsCount(res)
          }
        } else {
          console.warn("No organization or enterprise specified")
        }
      } catch (error) {
        LogError("Unable to count notifications", error)
        setErrorState("Unable to count notifications. See console for details.")
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      userCtx.participant_organizations,
      userCtx.participant_alias,
      userCtx.participant_create_date,
      pid,
    ],
  )

  useEffect(() => {
    getNotificationsCount()
  }, [userCtx.participant_organizations, getNotificationsCount])

  useEffect(() => {
    if (notificationsCount !== null) {
      getNotificationsCount()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshCount])

  useEffect(() => {
    if (errorState) {
      showError()
    }
  }, [errorState, showError])

  useEffect(() => {
    if (!!userCtx.notification_error) {
      showError(userCtx.notification_error)
      userCtx.setNotificationError(undefined)
    }
    if (!!userCtx.notification_Information) {
      showInfo(userCtx.notification_Information)
      userCtx.setNotificationInformation(undefined)
    }
    if (!!userCtx.notification_action) {
      setExternalAction(userCtx.notification_action)
      userCtx.setNotificationAction(undefined)
    }
  }, [showError, userCtx])

  // SignalR
  useEffect(() => {
    setupSignalRConnection()

    // Cleanup connection on unmount
    return () => {
      if (connection) {
        connection.stop()
        console.log("SignalR connection stopped.")
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  //Display variables:
  let icon = "notifications"

  if (
    userCtx.mutedNotifications !== null &&
    userCtx.mutedNotifications.channelSignalR
  ) {
    icon = "notifications_paused"
  }

  if (errorState !== null) {
    icon = "notifications_off"
  }

  return (
    <>
      <Toast ref={toast} position="top-right" />
      {notificationsDialogVisible && (
        <NotificationsDialog
          visible={notificationsDialogVisible}
          setVisible={setNotificationsDialogVisible}
          organizations={userCtx.participant_organizations}
          onShowError={showError}
          onHandleActionURL={handleActionURL}
          onRefreshBellCounter={setRefreshCount}
        />
      )}
      <li
        className="top-icon"
        onClick={() => {
          handleButtonClick()
        }}
      >
        <span className="material-icons p-overlay-badge">
          {icon}
          {notificationsCount && notificationsCount.numTotal > 0 && (
            <Badge
              value={
                window.innerWidth > 1024 ? notificationsCount.numTotal : null
              }
            />
          )}
        </span>
        <label className="top-menu-label">Notifications</label>
      </li>
    </>
  )
}
